/**
 * Location capture for the ToS acceptance gate. Best-available accuracy:
 *   1. Ask the browser for HTML5 Geolocation (GPS/Wi-Fi triangulation).
 *   2. On success, reverse-geocode the lat/lng via OpenStreetMap Nominatim
 *      to attach a human-readable address / city / state / country.
 *   3. On denial or failure, return a `{precision:"ip", consent_denied}`
 *      envelope so the server does an IP-based lookup as the fallback.
 *
 * The server enforces the "must have SOMETHING" rule — if neither precise
 * nor IP-based location resolves, the acceptance is rejected outright.
 */

const NOMINATIM = "https://nominatim.openstreetmap.org/reverse";
const GEO_TIMEOUT_MS = 12000;
const NOMINATIM_TIMEOUT_MS = 8000;

function withTimeout(promise, ms, label) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    promise.then((v) => { clearTimeout(t); resolve(v); },
                 (e) => { clearTimeout(t); reject(e); });
  });
}

async function browserGeolocate() {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return { ok: false, reason: "unsupported" };
  }
  try {
    const pos = await withTimeout(
      new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          maximumAge: 60_000,     // reuse recent fixes when possible
          timeout: GEO_TIMEOUT_MS - 500,
        });
      }),
      GEO_TIMEOUT_MS,
      "Browser geolocation",
    );
    return {
      ok: true,
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      accuracy_m: pos.coords.accuracy,
    };
  } catch (e) {
    // PERMISSION_DENIED = 1, POSITION_UNAVAILABLE = 2, TIMEOUT = 3
    const denied = e?.code === 1 || /denied/i.test(e?.message || "");
    return { ok: false, reason: denied ? "denied" : (e?.message || "failed") };
  }
}

async function reverseGeocode(lat, lng) {
  try {
    const url = `${NOMINATIM}?format=jsonv2&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}&addressdetails=1&zoom=18`;
    const res = await withTimeout(
      fetch(url, {
        headers: {
          // Nominatim expects a valid Referer/User-Agent so a real app can be identified.
          // The public API is fine with a plain Accept header from the browser.
          "Accept": "application/json",
        },
      }),
      NOMINATIM_TIMEOUT_MS,
      "Reverse geocode",
    );
    if (!res.ok) return null;
    const j = await res.json();
    const a = j.address || {};
    return {
      address: j.display_name || null,
      house_number: a.house_number || null,
      street: a.road || a.pedestrian || a.footway || a.residential || null,
      city: a.city || a.town || a.village || a.hamlet || a.suburb || null,
      region: a.state || a.region || null,
      postal_code: a.postcode || null,
      country: a.country || null,
      country_code: (a.country_code || "").toUpperCase() || null,
      reverse_source: "nominatim",
    };
  } catch {
    return null;
  }
}

/**
 * Public entry — returns the payload to attach to /tos/accept.
 * Never throws — always resolves with something the caller can send.
 */
export async function captureAcceptLocation() {
  const geo = await browserGeolocate();
  if (geo.ok) {
    const rev = await reverseGeocode(geo.lat, geo.lng);
    return {
      precision: "precise",
      lat: geo.lat,
      lng: geo.lng,
      accuracy_m: geo.accuracy_m,
      consent_denied: false,
      ...(rev || {}),
    };
  }
  return {
    precision: "ip",
    consent_denied: geo.reason === "denied",
  };
}
